if (!customElements.get('trusted-reviews-slider')) {
  customElements.define('trusted-reviews-slider', class TrustedReviewsSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-b-ts-track]');
      this.template = this.querySelector('[data-b-ts-card-template]');
      this.controls = this.querySelector('[data-b-ts-controls]');
      this.prev = this.querySelector('[data-b-ts-prev]');
      this.next = this.querySelector('[data-b-ts-next]');
      this.progress = this.querySelector('[data-b-ts-progress]');
      if (!this.track || !this.template) return;

      this.onScroll = this.update.bind(this);
      this.onResize = this.update.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));
      this.cancelConsent = window.BaechleinPrivacy?.whenAllowed('marketing', () => {
        if (this.isConnected) this.loadTrustedShopsReviews();
      });
      this.update();
    }

    disconnectedCallback() {
      this.cancelConsent?.();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    scrollByPage(direction) {
      const distance = this.track.clientWidth * 0.9 * direction;
      this.track.scrollBy({ left: distance, behavior: 'smooth' });
    }

    async loadTrustedShopsReviews() {
      const widgetId = this.dataset.widgetId;
      if (!widgetId) return;

      try {
        const manifestResponse = await fetch(
          `https://integrations.etrusted.com/manifests/v1/${encodeURIComponent(widgetId)}.json`,
          { credentials: 'omit' }
        );
        if (!manifestResponse.ok) return;

        const manifest = await manifestResponse.json();
        const reviewsFeedUrl = manifest.feeds?.review_carousel_service?.url;
        const gradesFeedUrl = manifest.feeds?.grades?.url;
        if (!reviewsFeedUrl || !gradesFeedUrl) return;

        if (!window.BaechleinPrivacy?.allowed('marketing')) return;
        const [reviewsResponse, gradesResponse] = await Promise.all([
          fetch(reviewsFeedUrl, { credentials: 'omit' }),
          fetch(gradesFeedUrl, { credentials: 'omit' })
        ]);
        if (!reviewsResponse.ok || !gradesResponse.ok) return;

        const [reviews, gradesFeed] = await Promise.all([
          reviewsResponse.json(),
          gradesResponse.json()
        ]);

        if (!Array.isArray(reviews) || reviews.length === 0) return;

        this.renderReviews(reviews);

        const publicGrade = gradesFeed.grades?.['365days'];
        const overallGrade = gradesFeed.grades?.overall;
        this.renderSummary(publicGrade, overallGrade);
        this.closest('.b-ts')?.querySelector('reveal-list')?.refreshItems?.();
        this.closest('.b-ts').hidden = false;
        requestAnimationFrame(() => this.update());
      } catch (error) {
        // The section stays hidden when Trusted Shops cannot provide reviews.
      }
    }

    renderReviews(reviews) {
      const fragment = document.createDocumentFragment();

      reviews.forEach((review) => {
        const card = this.template.content.firstElementChild.cloneNode(true);
        const title = card.querySelector('[data-b-ts-review-title]');
        const stars = card.querySelector('[data-b-ts-review-stars]');
        const text = card.querySelector('[data-b-ts-review-text]');
        const author = card.querySelector('[data-b-ts-review-author]');
        const verified = card.querySelector('[data-b-ts-review-verified]');

        if (this.dataset.profileUrl) card.href = this.dataset.profileUrl;
        else {
          card.removeAttribute('target');
          card.removeAttribute('rel');
        }
        title.textContent = review.title?.trim() || this.dataset.fallbackTitle;
        text.textContent = review.comment?.trim() || '';
        author.textContent = this.authorLine(review);
        this.renderStars(stars, review.rating);

        if (review.isVerified === true) {
          verified.hidden = false;
          verified.querySelector('[data-b-ts-verified-text]').textContent = this.dataset.verifiedLabel;
        }

        fragment.appendChild(card);
      });

      this.track.replaceChildren(fragment);
      this.dataset.reviewCount = String(reviews.length);
    }

    authorLine(review) {
      const consumer = review.consumer || {};
      const firstName = consumer.firstName?.trim();
      const lastName = consumer.lastName?.trim();
      const lastInitial = lastName ? `${lastName.charAt(0)}.` : '';
      const name = [firstName, lastInitial].filter(Boolean).join(' ') || this.dataset.fallbackAuthor;
      const city = consumer.city?.trim();
      const date = this.relativeDate(review.editedAt || review.submittedAt || review.date);
      return `${name}${city ? ` aus ${city}` : ''}${date ? `, ${date}` : ''}`;
    }

    relativeDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';

      const elapsedDays = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
      const locale = document.documentElement.lang || 'de-DE';
      const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      if (elapsedDays < 30) return formatter.format(-elapsedDays, 'day');
      if (elapsedDays < 365) return formatter.format(-Math.floor(elapsedDays / 30), 'month');
      return formatter.format(-Math.floor(elapsedDays / 365), 'year');
    }

    renderStars(container, value) {
      if (!container) return;

      const rating = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
      container.replaceChildren();
      container.setAttribute('aria-label', `${rating} von 5 Sternen`);
      for (let index = 1; index <= 5; index += 1) {
        const star = document.createElement('span');
        star.setAttribute('aria-hidden', 'true');
        star.textContent = index <= rating ? '★' : '☆';
        container.appendChild(star);
      }
    }

    renderSummary(publicGrade, overallGrade) {
      if (!publicGrade) return;

      const locale = document.documentElement.lang || 'de-DE';
      const rating = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(publicGrade.rating);
      const reviewCount = new Intl.NumberFormat(locale).format(overallGrade?.count || publicGrade.count);
      const summaryRating = this.closest('.b-ts')?.querySelector('[data-b-ts-summary-rating]');
      const summaryStars = this.closest('.b-ts')?.querySelector('[data-b-ts-summary-stars]');

      if (summaryRating) {
        summaryRating.textContent = `${rating} von 5 Sternen - basierend auf ${reviewCount} Bewertungen`;
      }
      if (summaryStars) summaryStars.setAttribute('aria-label', `${rating} von 5 Sternen`);
    }

    update() {
      const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const progress = max > 0 ? this.track.scrollLeft / max : 0;
      const width = max > 0 ? Math.max(this.track.clientWidth / this.track.scrollWidth, 0.18) : 1;
      if (this.controls) this.controls.hidden = max <= 2;
      if (this.progress) {
        this.progress.style.width = (width * 100) + '%';
        this.progress.style.transform = 'translateX(' + (progress * (100 / width - 100)) + '%)';
      }
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    }
  });
}
